import React, { Suspense, lazy, useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppProvider, useApp } from './context/AppContext';
import { updateLastActive } from './utils/sessionExpiry';
import { NotificationProvider, useNotification } from './context/NotificationContext';
import NotificationContainer from './components/Notification';
import { ConfirmationModal } from './components/ConfirmationModal';
import BottomNav from './components/BottomNav';
import Header from './components/Header';
import IOSInstallPrompt from './components/IOSInstallPrompt';
import { Loader2 } from 'lucide-react';

// Lazy load pages for optimized loading speed
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Groups = lazy(() => import('./pages/Groups'));
const GroupDetails = lazy(() => import('./pages/GroupDetails'));
const AddExpense = lazy(() => import('./pages/AddExpense'));
const Activity = lazy(() => import('./pages/Activity'));
const Account = lazy(() => import('./pages/Account'));
const Friends = lazy(() => import('./pages/Friends'));
const Settings = lazy(() => import('./pages/Settings'));
const Privacy = lazy(() => import('./pages/Privacy'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const HelpCenter = lazy(() => import('./pages/HelpCenter'));
const Login = lazy(() => import('./pages/Login'));
const SearchPage = lazy(() => import('./pages/Search'));
const JoinGroup = lazy(() => import('./pages/JoinGroup'));
const About = lazy(() => import('./pages/About'));
const Analytics = lazy(() => import('./pages/Analytics'));

const PageLoader = () => (
  <div className="flex flex-col items-center justify-center h-full bg-white dark:bg-gray-900">
    <Loader2 size={32} className="animate-spin text-brand-600 dark:text-brand-400" />
  </div>
);

// Component to handle PWA updates especially for iOS
const ServiceWorkerUpdateHandler: React.FC = () => {
  const { addNotification } = useNotification();
  const { t } = useApp();

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      try {
        const handleControllerChange = () => {
            addNotification(t('updating_to_latest'), "info");
            setTimeout(() => {
                window.location.reload();
            }, 1000);
        };
        
        navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

        const checkUpdate = () => {
          try {
            navigator.serviceWorker.getRegistration().then(reg => {
              if (reg) {
                // Surgical fix: Gracefully handle Cloud Workstation / environment-specific SW errors
                reg.update().catch(err => {
                   if (err.name === 'InvalidStateError') {
                      console.debug('Service Worker update ignored in current state');
                   } else {
                      console.debug('SW update check failed', err);
                   }
                });
              }
            }).catch(err => {
              console.debug('Service Worker registration access denied:', err.message);
            });
          } catch (err) {
            console.debug('Service Worker update check skipped due to environment restrictions');
          }
        };

        const handleVisibilityChange = () => {
          if (document.visibilityState === 'visible') {
            checkUpdate();
          }
        };

        checkUpdate();
        document.addEventListener('visibilitychange', handleVisibilityChange);
        const interval = setInterval(checkUpdate, 30 * 60 * 1000);

        return () => {
          document.removeEventListener('visibilitychange', handleVisibilityChange);
          navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
          clearInterval(interval);
        };
      } catch (e) {
        console.debug('Service Worker API not available in this environment');
      }
    }
  }, [addNotification, t]);

  return null;
};

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { confirmation, closeConfirmation } = useApp();
  const path = location.pathname;

  // Track page navigation as an activity to extend session
  useEffect(() => {
    updateLastActive();
  }, [location]);

  const isEditExpense = path.startsWith('/edit-expense/');
  const isAddExpense = path === '/add-expense';
  const isJoinRoute = path.startsWith('/join/');
  
  const showBottomNav = path !== '/login' && !isJoinRoute && !isAddExpense && !isEditExpense;
  const hideHeaderPaths = ['/add-expense', '/login', '/settings', '/privacy', '/help', '/search', '/about', '/analytics'];
  const showHeader = !hideHeaderPaths.includes(path) && !path.startsWith('/group/') && !isEditExpense && !isJoinRoute;

  return (
    <div className="fixed inset-0 flex flex-col max-w-md mx-auto bg-white dark:bg-gray-900 shadow-2xl overflow-hidden border-x border-gray-200 dark:border-gray-800 transition-colors duration-200">
      {showHeader && <div className="flex-shrink-0 z-40"><Header /></div>}
      <main className={`flex-1 overflow-y-auto no-scrollbar bg-gray-50 dark:bg-gray-900 transition-colors duration-200 min-h-0 ${showBottomNav ? 'mb-16' : ''}`}>
        <Suspense fallback={<PageLoader />}>
          {children}
        </Suspense>
      </main>
      {showBottomNav && <div className="flex-shrink-0 z-40"><BottomNav /></div>}
      <NotificationContainer />
      <IOSInstallPrompt />
      <ConfirmationModal 
        isOpen={confirmation.isOpen}
        onClose={closeConfirmation}
        onConfirm={confirmation.onConfirm}
        title={confirmation.title}
        message={confirmation.message}
        confirmText={confirmation.confirmText}
        cancelText={confirmation.cancelText}
      />
    </div>
  );
};

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isSessionChecking } = useApp();
  const location = useLocation();
  
  if (isSessionChecking && !isAuthenticated) {
      return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <>{children}</>;
};

const AppContent: React.FC = () => {
    const { addNotification } = useNotification();
    return (
        <AppProvider addNotification={addNotification}>
          <ServiceWorkerUpdateHandler />
          <Layout>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/PrivacyPolicy" element={<PrivacyPolicy />} />
              <Route path="/" element={<RequireAuth><Navigate to="/dashboard" replace /></RequireAuth>} />
              <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
              <Route path="/groups" element={<RequireAuth><Groups /></RequireAuth>} />
              <Route path="/friends" element={<RequireAuth><Friends /></RequireAuth>} />
              <Route path="/group/:groupId" element={<RequireAuth><GroupDetails /></RequireAuth>} />
              <Route path="/join/:groupId" element={<RequireAuth><JoinGroup /></RequireAuth>} />
              <Route path="/add-expense" element={<RequireAuth><AddExpense /></RequireAuth>} />
              <Route path="/edit-expense/:expenseId" element={<RequireAuth><AddExpense /></RequireAuth>} />
              <Route path="/activity" element={<RequireAuth><Activity /></RequireAuth>} />
              <Route path="/analytics" element={<RequireAuth><Analytics /></RequireAuth>} />
              <Route path="/account" element={<RequireAuth><Account /></RequireAuth>} />
              <Route path="/search" element={<RequireAuth><SearchPage /></RequireAuth>} />
              <Route path="/settings" element={<RequireAuth><Settings /></RequireAuth>} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/help" element={<RequireAuth><HelpCenter /></RequireAuth>} />
              <Route path="/about" element={<RequireAuth><About /></RequireAuth>} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Layout>
        </AppProvider>
    );
};

const App: React.FC = () => {
  return (
    <NotificationProvider>
      <HashRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AppContent />
      </HashRouter>
    </NotificationProvider>
  );
};

export default App;